import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

const QUERY_KEY = ['sidebar-layout'] as const;

/**
 * Lit l'ordre custom des items top-level de la sidebar (singleton DB).
 * Retourne un array d'IDs de NavItem ; tableau vide tant qu'aucun ordre
 * n'a été sauvegardé.
 *
 * Défensif : si la table n'existe pas encore (migration 20260509_003 pas
 * roulée), on retourne [] silencieusement plutôt que de planter la
 * sidebar pour tout le monde.
 */
export function useSidebarLayout() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<string[]> => {
      try {
        const { data, error } = await supabase
          .from('sidebar_layout')
          .select('item_order')
          .eq('id', 'global')
          .maybeSingle();
        if (error) {
          // Table inexistante / colonne manquante / RLS qui bloque → fallback
          return [];
        }
        const order = (data?.item_order ?? []) as unknown;
        if (!Array.isArray(order)) return [];
        return order.filter((x): x is string => typeof x === 'string');
      } catch {
        return [];
      }
    },
    // Refresh toutes les 30s — un autre client peut avoir réorganisé
    staleTime: 30_000,
  });
}

/**
 * Sauvegarde un nouvel ordre. RLS limite l'INSERT/UPDATE aux owner/admin
 * (la table le bloque côté DB de toute façon).
 */
export function useUpdateSidebarLayout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (order: string[]): Promise<void> => {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id ?? null;

      const { error } = await supabase.from('sidebar_layout').upsert({
        id: 'global',
        item_order: order,
        updated_by: userId,
        updated_at: new Date().toISOString(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
